import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowsClockwise, CheckCircle, Warning, XCircle, Camera } from "@phosphor-icons/react";
import { http } from "@/lib/api";

// Sprint 62.8 — Week-1 preflight checklist (admin). Feeds · projections ·
// ownership · field model · pre-lock snapshot → pass / warn / fail.
const STYLE = {
  pass: { Icon: CheckCircle, cls: "text-[#39FF14]", border: "border-[#39FF14]/40" },
  warn: { Icon: Warning, cls: "text-[#FFCC00]", border: "border-[#FFCC00]/50" },
  fail: { Icon: XCircle, cls: "text-[#FF3B30]", border: "border-[#FF3B30]/60" },
};

export function usePreflight() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try { const r = await http.get("/admin/preflight"); setData(r.data); }
    catch { setData(null); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);
  const snapshot = async () => {
    setBusy(true);
    try {
      const r = await http.post("/admin/calibration/snapshot");
      toast.success(`Pre-lock snapshot saved · ${r.data.snapshots} slate${r.data.snapshots === 1 ? "" : "s"}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Snapshot failed"); setBusy(false); }
  };
  return { data, busy, load, snapshot };
}

export default function PreflightPanel({ compact = false }) {
  const { data, busy, load, snapshot } = usePreflight();
  const overall = data?.overall || "fail";
  const S = STYLE[overall];
  const checks = data?.checks || [];
  if (compact) {
    return (
      <div className={`border ${S.border} bg-black/40 px-3 py-2 flex items-center gap-3 flex-wrap`} data-testid="preflight-strip">
        <span className={`inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-widest font-bold ${S.cls}`} data-testid="preflight-overall">
          <S.Icon size={14} weight="fill" /> Preflight {overall}
        </span>
        <div className="flex items-center gap-1.5 flex-wrap">
          {checks.map((c) => {
            const T = STYLE[c.status];
            return (
              <span key={c.id} title={`${c.label}: ${c.detail}`} data-testid={`preflight-chip-${c.id}`}
                className={`inline-flex items-center gap-1 mono text-[9px] uppercase tracking-wider ${T.cls} border ${T.border} px-1.5 py-0.5`}>
                <T.Icon size={10} weight="fill" /> {c.label.split(" (")[0]}
              </span>
            );
          })}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button type="button" onClick={snapshot} disabled={busy} data-testid="preflight-snapshot-btn"
            className="inline-flex items-center gap-1 mono text-[9px] uppercase tracking-widest text-zinc-300 hover:text-[#39FF14] border border-zinc-800 hover:border-[#39FF14]/60 px-2 py-1 disabled:opacity-50">
            <Camera size={11} /> Snapshot now
          </button>
          <button type="button" onClick={load} disabled={busy} data-testid="preflight-refresh-btn" title="Re-run checks"
            className="text-zinc-500 hover:text-white disabled:opacity-50">
            <ArrowsClockwise size={12} className={busy ? "animate-spin" : ""} />
          </button>
        </div>
      </div>
    );
  }
  return (
    <section className={`bg-[#121212] border ${S.border} p-4`} data-testid="admin-preflight-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="heading text-lg font-black uppercase inline-flex items-center gap-2">
            <S.Icon size={18} weight="fill" className={S.cls} /> Week-1 Preflight
            <span className={`mono text-[10px] tracking-widest ${S.cls}`} data-testid="preflight-overall">{overall}</span>
          </div>
          <div className="text-[11px] text-zinc-400 mt-1 max-w-3xl leading-relaxed">
            Run before every lock. Green across the board = feeds fresh, injuries synced, field model loaded on ≥50% of the playable pool,
            ownership shares sane, pre-lock snapshot frozen for Monday's grading.
            {data?.counts && <span className="text-zinc-500"> · {data.counts.pass} pass / {data.counts.warn} warn / {data.counts.fail} fail</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={snapshot} disabled={busy} data-testid="preflight-snapshot-btn"
            className="inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-widest bg-[#39FF14] hover:bg-[#2ee30d] disabled:opacity-50 text-black font-black px-3 py-2">
            <Camera size={12} weight="fill" /> Take snapshot now
          </button>
          <button type="button" onClick={load} disabled={busy} data-testid="preflight-refresh-btn"
            className="inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-widest text-zinc-300 hover:text-white border border-zinc-800 px-3 py-2 disabled:opacity-50">
            <ArrowsClockwise size={12} className={busy ? "animate-spin" : ""} /> Re-run
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {checks.map((c) => {
          const T = STYLE[c.status];
          return (
            <div key={c.id} className={`border ${T.border} bg-black/30 px-3 py-2 flex items-start gap-2`} data-testid={`preflight-check-${c.id}`}>
              <T.Icon size={16} weight="fill" className={`${T.cls} mt-0.5 flex-shrink-0`} />
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-white text-[12px] font-bold">{c.label}</span>
                  <span className={`mono text-[9px] uppercase tracking-widest ${T.cls}`} data-testid={`preflight-status-${c.id}`}>{c.status}</span>
                </div>
                <div className="text-[11px] text-zinc-400 leading-snug" data-testid={`preflight-detail-${c.id}`}>{c.detail}</div>
                {c.id === "ownership" && c.sums && (
                  <div className="mono text-[9px] text-zinc-600 mt-0.5">{Object.entries(c.sums).map(([k, v]) => `${k} ${v}`).join(" · ")}</div>
                )}
                {c.id === "field_model" && c.coverage?.weights?.n_weeks > 0 && (
                  <div className="mono text-[9px] text-zinc-600 mt-0.5">
                    trust {Object.entries(c.coverage.weights.proj || {}).map(([k, v]) => `${k} ${Math.round(v * 100)}%`).join(" · ")}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {!data && !busy && <div className="text-[11px] text-zinc-500" data-testid="preflight-empty">Preflight unavailable.</div>}
      </div>
      {data?.generated_at && <div className="mono text-[9px] text-zinc-600 mt-2">checked {new Date(data.generated_at).toLocaleTimeString()}</div>}
    </section>
  );
}
